import { GoogleOutlined } from '@ant-design/icons'
import { useMutation } from '@apollo/client'
import React, { useState } from 'react'
import {
  GoogleLoginResponse,
  useGoogleLogin,
  useGoogleLogout,
} from 'react-google-login'
import { RouteComponentProps } from 'react-router-dom'

import Button from 'components/atoms/Button'
import Card from 'components/atoms/Card'
import Divider from 'components/atoms/Divider'
import EquifyImage from 'components/atoms/EquifyImage'
import ErrorOrDefault from 'components/atoms/ErrorAlert/ErrorOrDefault'
import FakeCenter from 'components/atoms/FakeCenter'
import Form, { useForm } from 'components/atoms/Form'
import FormItem from 'components/atoms/Form/FormItem'
import Input from 'components/atoms/Form/Input'
import Password from 'components/atoms/Form/Password'
import SelectLocale from 'components/molecules/Select/Locale'
import { REACT_APP_GOOGLE_API_KEY } from 'config/env'
import { useIsMobileWindowSize } from 'hooks/useWindowSize'
import { LOGIN } from 'providers/Auth/graphql/mutations/LOGIN'
import { TRADE_IN_GOOGLE_TOKENS } from 'providers/Auth/graphql/mutations/TRADE_IN_GOOGLE_TOKENS'
import { useToken } from 'providers/Token'
import { css, x } from 'utils/emotion'
import { isDevEnv } from 'utils/env'

function Login({ history }: RouteComponentProps) {
  const [login] = useMutation(LOGIN)
  const [tradeInGoogleTokens] = useMutation(TRADE_IN_GOOGLE_TOKENS)
  const isMobileWindowSize = useIsMobileWindowSize()
  const [form] = useForm()
  const { setToken } = useToken()

  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(false)

  const { signOut: googleSignOut } = useGoogleLogout({
    clientId: REACT_APP_GOOGLE_API_KEY,
    onLogoutSuccess: () => {},
    onFailure: () => {},
  })

  const { signIn: googleSignIn } = useGoogleLogin({
    clientId: REACT_APP_GOOGLE_API_KEY,
    cookiePolicy: 'single_host_origin',
    isSignedIn: true,
    onRequest: () => {
      setError(null)
    },
    // @ts-ignore
    onSuccess: (response: GoogleLoginResponse) => {
      googleSignOut()
      tradeInGoogleTokens({
        variables: {
          accessToken: response.accessToken,
          idToken: response.tokenId,
        },
      })
        .then(({ data }) => {
          if (data?.tradeInGoogleTokens) {
            setToken(data.tradeInGoogleTokens)
          }
        })
        .catch(setError)
    },
    onFailure: (err) => {
      switch (err?.error) {
        case 'idpiframe_initialization_failed':
          setError({
            // @ts-ignore
            message: 'auth.thirdPartyCookies',
          })
          break
        case 'popup_closed_by_user':
          break
        default:
          setError(err)
      }
    },
  })

  const validFormAction = ({
    email,
    password,
  }: {
    email: string
    password: string
  }) => {
    setError(null)
    setLoading(true)
    login({
      variables: { email, password },
    })
      .then(({ data }) => {
        if (data?.login) {
          setToken(data.login)
        }
      })
      .catch((error) => {
        if (error?.message === 'EMAIL_NOT_VERIFIED') {
          history.push(`/verify-email/${email}`)
        } else {
          setError(error)
        }
      })
      .finally(() => {
        setLoading(false)
      })
  }

  return (
    <FakeCenter>
      <EquifyImage />
      <Card
        title="word.login"
        style={{ width: isMobileWindowSize ? 'fit-content' : '400px' }}
      >
        <Form
          form={form}
          layout="vertical"
          name="login"
          onFinish={validFormAction}
          initialValues={
            isDevEnv
              ? { email: 'admin@equify.eu', password: 'password' }
              : undefined
          }
        >
          {error && (
            <x.div marginBottom="xlarge">
              <ErrorOrDefault error={error} afterClose={() => setError(null)} />
            </x.div>
          )}
          <Button
            block
            size="large"
            icon={<GoogleOutlined />}
            type="primary"
            message="word.loginWithGoogle"
            disabled={loading}
            onClick={googleSignIn}
          />
          <Divider message="word.or" plain />
          <FormItem
            name="email"
            label="word.emailAddress"
            rules={[{ required: true }]}
          >
            <Input size="large" placeholder="auth.enterYourEmailAddress" />
          </FormItem>
          <FormItem
            style={{ marginBottom: 8 }}
            name="password"
            label="word.password"
            rules={[{ required: true }]}
          >
            <Password size="large" />
          </FormItem>
          <FormItem>
            <Button
              type="text"
              size="small"
              to="/reset-password"
              message="auth.forgotYourPassword"
            />
          </FormItem>
          <FormItem>
            <Button
              block
              size="large"
              type="primary"
              htmlType="submit"
              message="word.login"
              loading={loading}
            />
          </FormItem>
        </Form>
      </Card>
      <x.div
        w="100px"
        marginTop="xlarge"
        marginLeft="auto"
        marginRight="auto"
        marginBottom="xlarge"
        className={css`
          .eq-select {
            width: 100%;
            text-align: center;
          }
        `}
      >
        <SelectLocale bordered={false} />
      </x.div>
    </FakeCenter>
  )
}

export default React.memo(Login)
